import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  clearAuthStorage,
  EXPIRES_AT_KEY,
  installAuthRefreshInterceptor,
  REMEMBER_KEY,
  SESSION_ONLY_KEY,
  TOKEN_KEY,
  USER_KEY,
} from "./sessionRefresh";

/**
 * Cookie-based auth — backend sets an httpOnly session cookie on login.
 * We keep only a small `user` payload in localStorage for instant boot.
 */
const REMEMBER_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

axios.defaults.withCredentials = true;
installAuthRefreshInterceptor(axios);

const clearExpiredRememberedAuth = () => {
  try {
    const expiresAt = Number(localStorage.getItem(EXPIRES_AT_KEY) || 0);
    if (expiresAt && Date.now() > expiresAt) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REMEMBER_KEY);
      localStorage.removeItem(EXPIRES_AT_KEY);
      localStorage.removeItem(SESSION_ONLY_KEY);
    }
  } catch {
    // Ignore storage availability issues.
  }
};

const activeStorage = () => {
  clearExpiredRememberedAuth();
  try {
    if (localStorage.getItem(USER_KEY)) return localStorage;
    if (sessionStorage.getItem(USER_KEY)) return sessionStorage;
  } catch {
    return null;
  }
  return null;
};

const readToken = () => {
  try {
    return activeStorage()?.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

const readUser = () => {
  try {
    const raw = activeStorage()?.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/* ---------- Default landing page per role ---------- */
export const DEFAULT_PAGE_BY_ROLE = {
  administration: "/home",
  operations: "/home",
  finance: "/finance",
};
export const normalizeRole = (role) =>
  typeof role === "string" ? role.trim().toLowerCase() : "";
export const defaultPageFor = (role) =>
  DEFAULT_PAGE_BY_ROLE[normalizeRole(role)] || "/home";

/* ---------- Role gating ---------- */
const ALL_PAGES = new Set(["home", "create", "bookings", "products", "finance", "stream", "shop", "support", "refunds"]);

const NAV_ACCESS = {
  administration: ALL_PAGES,
  operations:     new Set(["home", "create", "bookings", "products", "stream", "shop", "support", "refunds"]),
  finance:        new Set(["finance", "shop", "support", "refunds"]),
};

export const canAccess = (role, key) =>
  NAV_ACCESS[normalizeRole(role)]?.has(key) ?? false;

// True for roles that can access account management and legal pages
export const isPrivileged = (role) =>
  normalizeRole(role) === "administration";

export const useAuth = () => {
  const [token, setToken] = useState(() => readToken());
  const [user, setUser] = useState(() => readUser());

  useEffect(() => {
    const sync = (e) => {
      if (
        !e ||
        e.key === TOKEN_KEY ||
        e.key === USER_KEY ||
        e.key === REMEMBER_KEY ||
        e.key === EXPIRES_AT_KEY ||
        e.type === "voocly:auth"
      ) {
        setToken(readToken());
        setUser(readUser());
      }
    };
    window.addEventListener("storage", sync);
    window.addEventListener("voocly:auth", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("voocly:auth", sync);
    };
  }, []);

  const login = useCallback(({ user: u, remember = false }) => {
    clearAuthStorage();
    const storage = localStorage;
    if (u) storage.setItem(USER_KEY, JSON.stringify(u));
    if (remember) {
      storage.setItem(REMEMBER_KEY, "true");
      storage.setItem(EXPIRES_AT_KEY, String(Date.now() + REMEMBER_DURATION_MS));
    } else {
      storage.setItem(SESSION_ONLY_KEY, "true");
    }
    window.dispatchEvent(new Event("voocly:auth"));
    setToken(null);
    if (u) setUser(u);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`);
    } catch {
      // Clear local UI state even if the server session is already gone.
    }
    clearAuthStorage();
    window.dispatchEvent(new Event("voocly:auth"));
    setToken(null);
    setUser(null);
  }, []);

  return { user, token, role: normalizeRole(user?.role) || null, login, logout };
};

/**
 * Conditional render: only show children when the current user's role is
 * in the `roles` whitelist. Defaults to "always render" when role unknown.
 */
export const IfRole = ({ roles, children, fallback = null }) => {
  const { role } = useAuth();
  if (!roles || roles.length === 0) return children;
  if (!role) return fallback;
  return roles.includes(role) ? children : fallback;
};
